// The stock page's intraday chart on TradingView's Lightweight Charts: a standard price scale,
// time axis, crosshair and scroll/zoom, with MoneyHQ's layers on top (VWAP, order-block zones,
// swing levels, classic pivots, liquidity, sweeps, volume profile, volume, and an order-flow pane).
import {
  CandlestickSeries, ColorType, CrosshairMode, HistogramSeries, LineSeries, LineStyle, PriceScaleMode, createChart, createSeriesMarkers,
  type IChartApi, type IPriceLine, type IPrimitivePaneRenderer, type IPrimitivePaneView, type ISeriesApi,
  type ISeriesMarkersPluginApi, type ISeriesPrimitive, type SeriesAttachedParameter, type Time, type UTCTimestamp,
} from 'lightweight-charts';
import { chartPatterns, classicPivots, zoneLabel, type Analysed, type ChartPattern, type Bar, type Detail, type Market, type OrderBlock, type SRKey } from '@planb/core';
import { PatternLayer, type PatternDraw } from './pattern-layer';
import { TradeLayer, type TradeDraw } from './trade-layer';
import { PlanMarks, type PlanMark } from './marks';
import { palette } from './palette';
import { fmtVol } from './stock-chart';

type CanvasRenderingTarget2D = Parameters<IPrimitivePaneRenderer['draw']>[0];

export type ProLayer = 'vwap' | 'zones' | 'levels' | 'pivots' | 'liquidity' | 'sweeps' | 'profile' | 'volume' | 'flow';
export const PRO_LAYERS: { id: ProLayer; label: string }[] = [
  { id: 'vwap', label: 'VWAP' },
  { id: 'zones', label: 'Order blocks' },
  { id: 'levels', label: 'Swing levels' },
  { id: 'pivots', label: 'Pivots' },
  { id: 'liquidity', label: 'Liquidity' },
  { id: 'sweeps', label: 'Sweeps' },
  { id: 'profile', label: 'Volume profile' },
  { id: 'volume', label: 'Volume' },
  { id: 'flow', label: 'Order flow' },
];
export const DEFAULT_PRO_LAYERS = new Set<ProLayer>(['vwap', 'zones', 'levels', 'sweeps', 'volume', 'flow']);

/** Our own swing levels, named so they don't read as pivot points (which R1/S1 usually mean). */
const SWING_NAME: Record<SRKey, string> = { R1: 'SH1', R2: 'SH2', S1: 'SL1', S2: 'SL2' };

/** Bar i's time: exchange wall-clock time written as UTC, so the axis reads in IST anywhere. */
const barTime = (date: string, market: Market, i: number) => {
  const [y, m, d] = date.split('-').map(Number);
  return (Date.UTC(y, m - 1, d) / 1000 + (market.openMinute + i * market.barMinutes) * 60) as UTCTimestamp;
};

const signedVol = (v: number) => `${v < 0 ? '−' : ''}${fmtVol(Math.abs(v))}`;

const alpha = (hex: string, a: number) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

// ── Order-block zones: rectangles from the block's candle to where it broke (or now) ──────────

interface Box { from: Time; to: Time; top: number; bottom: number; color: string; fill: number; dashed: boolean; label: string; selected: boolean;
  /** A wide zone also shows its source candle's full range, faintly. */
  full?: { top: number; bottom: number } }

class ZonesPrimitive implements ISeriesPrimitive<Time> {
  boxes: Box[] = [];
  private param: SeriesAttachedParameter<Time> | null = null;
  attached(p: SeriesAttachedParameter<Time>) { this.param = p; }
  detached() { this.param = null; }
  set(boxes: Box[]) { this.boxes = boxes; this.param?.requestUpdate(); }
  paneViews(): readonly IPrimitivePaneView[] {
    const view: IPrimitivePaneView = { zOrder: () => 'bottom', renderer: () => this.renderer() };
    return [view];
  }
  private renderer(): IPrimitivePaneRenderer | null {
    const p = this.param;
    if (!p) return null;
    const ts = p.chart.timeScale(), series = p.series;
    const boxes = this.boxes;
    return {
      draw: (target: CanvasRenderingTarget2D) => {
        target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr, verticalPixelRatio: vr, bitmapSize }) => {
          for (const b of boxes) {
            const x1 = ts.timeToCoordinate(b.from), y1 = series.priceToCoordinate(b.top), y2 = series.priceToCoordinate(b.bottom);
            let x2 = ts.timeToCoordinate(b.to);
            if (x1 === null || y1 === null || y2 === null) continue;
            if (x2 === null) x2 = (bitmapSize.width / hr) as typeof x1;
            const X = x1 * hr, Y = Math.min(y1, y2) * vr, W = Math.max(2, (x2 - x1) * hr), H = Math.max(2, Math.abs(y2 - y1) * vr);
            if (b.full) {
              const f1 = series.priceToCoordinate(b.full.top), f2 = series.priceToCoordinate(b.full.bottom);
              if (f1 !== null && f2 !== null) {
                ctx.strokeStyle = alpha(b.color, 0.3);
                ctx.lineWidth = Math.max(1, hr);
                ctx.setLineDash([2 * hr, 4 * hr]);
                ctx.strokeRect(X, Math.min(f1, f2) * vr, W, Math.abs(f2 - f1) * vr);
                ctx.setLineDash([]);
              }
            }
            ctx.fillStyle = alpha(b.color, b.fill);
            ctx.fillRect(X, Y, W, H);
            ctx.strokeStyle = alpha(b.color, b.dashed ? 0.35 : b.selected ? 1 : 0.75);
            ctx.lineWidth = Math.max(1, hr) * (b.selected ? 2 : 1);
            ctx.setLineDash(b.dashed ? [3 * hr, 3 * hr] : []);
            ctx.strokeRect(X, Y, W, H);
            ctx.setLineDash([]);
            // Label inside the box at its left edge (above it when the box is too thin), so labels
            // of nearby zones don't pile up at the right edge.
            ctx.font = `600 ${10 * vr}px "JetBrains Mono Variable", ui-monospace, monospace`;
            ctx.fillStyle = alpha(b.color, 0.95);
            ctx.textAlign = 'left';
            ctx.fillText(b.label, X + 4 * hr, H >= 14 * vr ? Y + 11 * vr : Y - 3 * vr);
          }
        });
      },
    };
  }
}

// ── Volume profile: today's volume by price, buy and sell split, against the right edge ─────────

interface ProfileRow { top: number; bottom: number; buy: number; sell: number }

class ProfilePrimitive implements ISeriesPrimitive<Time> {
  rows: ProfileRow[] = [];
  poc = -1;
  colors = { buy: '#16a34a', sell: '#dc2626' };
  private param: SeriesAttachedParameter<Time> | null = null;
  attached(p: SeriesAttachedParameter<Time>) { this.param = p; }
  detached() { this.param = null; }
  set(rows: ProfileRow[], poc: number, colors: { buy: string; sell: string }) { this.rows = rows; this.poc = poc; this.colors = colors; this.param?.requestUpdate(); }
  paneViews(): readonly IPrimitivePaneView[] {
    return [{ zOrder: () => 'bottom', renderer: () => this.renderer() }];
  }
  private renderer(): IPrimitivePaneRenderer | null {
    const p = this.param;
    if (!p || !this.rows.length) return null;
    const series = p.series, rows = this.rows, poc = this.poc, col = this.colors;
    const max = Math.max(...rows.map((r) => r.buy + r.sell)) || 1;
    return {
      draw: (target: CanvasRenderingTarget2D) => {
        target.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hr, verticalPixelRatio: vr, bitmapSize }) => {
          // At most a fifth of the plot, so the latest candles stay readable.
          const right = bitmapSize.width, full = Math.min(bitmapSize.width * 0.2, 160 * hr);
          rows.forEach((r, k) => {
            const y1 = series.priceToCoordinate(r.top), y2 = series.priceToCoordinate(r.bottom);
            if (y1 === null || y2 === null) return;
            const Y = Math.min(y1, y2) * vr, H = Math.max(1, Math.abs(y2 - y1) * vr - vr);
            const len = (full * (r.buy + r.sell)) / max, bw = (len * r.buy) / ((r.buy + r.sell) || 1);
            const a = k === poc ? 0.6 : 0.35;
            ctx.fillStyle = alpha(col.buy, a);
            ctx.fillRect(right - len, Y, bw, H);
            ctx.fillStyle = alpha(col.sell, a);
            ctx.fillRect(right - len + bw, Y, len - bw, H);
          });
        });
      },
    };
  }
}

/** Groups 5-minute bars into k-bar candles (k = 3 for 15 minutes); the last one may be partial. */
export function aggregate(bars: Bar[], k: number): Bar[] {
  if (k <= 1) return bars;
  const out: Bar[] = [];
  for (let i = 0; i < bars.length; i += k) {
    const g = bars.slice(i, i + k);
    const sum = (f: (b: Bar) => number | null | undefined) => g.reduce((x, b) => x + (f(b) ?? 0), 0);
    out.push({
      o: g[0].o, h: Math.max(...g.map((b) => b.h)), l: Math.min(...g.map((b) => b.l)), c: g[g.length - 1].c,
      v: sum((b) => b.v), oi: g[g.length - 1].oi, bv: sum((b) => b.bv), sv: sum((b) => b.sv),
    });
  }
  return out;
}

export interface ProChartData {
  a: Analysed; d: Detail; t: number; market: Market; date: string;
  /** Previous session's high / low / close, for the classic pivots. */
  prev?: { high?: number; low?: number; close: number };
  layers: Set<ProLayer>;
  /** Candle size in 5-minute bars: 1 (5m) or 3 (15m). Analysis stays on 5-minute bars. */
  k?: number;
  /** Earlier sessions' candles at the same size, drawn plain to the left of today (times as on the axis). */
  past?: PastCandle[];
  /** false while this timeframe's earlier sessions are still loading (a pending window waits for them). */
  pastLoaded?: boolean;
  /** Detect and draw classic chart patterns over every candle shown (earlier sessions and today's). */
  patterns?: boolean;
  /** Patterns are found from this time on (a fixed start, so scrolling back for more history doesn't
   * change them: swing points depend on where the count starts). */
  patternsFrom?: number;
}
export interface PastCandle { t: number; o: number; h: number; l: number; c: number; v: number }
/** A visible window, kept across timeframe switches: right-edge time, width in candles, at the live end. */
export interface Viewport { right: number; bars: number; atEnd: boolean }

/** One chart instance per stock page; update() redraws the layers without resetting the view. */
export class ProChart {
  /** Called with the zone a click landed in (or null); set by the page. */
  onZone: ((z: OrderBlock | null) => void) | null = null;
  private selected: number | null = null;
  private shown: OrderBlock[] = [];
  readonly chart: IChartApi;
  private candles: ISeriesApi<'Candlestick'>;
  private volume: ISeriesApi<'Histogram'>;
  private vwap: ISeriesApi<'Line'>;
  private delta: ISeriesApi<'Histogram'> | null = null;
  private cvd: ISeriesApi<'Line'> | null = null;
  private lines: IPriceLine[] = [];
  private zones = new ZonesPrimitive();
  private profile = new ProfilePrimitive();
  private markers: ISeriesMarkersPluginApi<Time>;
  private fitted = false;
  private refitUntil = 0;
  private last: ProChartData | null = null;
  /** Earlier sessions' candles before today's first one: today's candle j sits at logical index offset + j. */
  offset = 0;
  private slots = 0;
  // Earlier sessions live in their own series, so live ticks redraw only today's candles.
  private pastCandles: ISeriesApi<'Candlestick'>;
  private pastVolume: ISeriesApi<'Histogram'>;
  private pastData: PastCandle[] = [];
  private lastPast: PastCandle[] | undefined;
  private lastK = 0;
  private lastDate = '';
  private pending: Viewport | null = null;
  private pendingSessions: number | null = null;
  /** Called when the view nears the oldest loaded candle (load more history). */
  onNeedMore: (() => void) | null = null;
  private patternLayer: PatternLayer;
  private planMarks!: PlanMarks;
  private tradeLayer!: TradeLayer;
  private tradeDraw: TradeDraw[] = [];
  /** Markers on earlier sessions' candles (a marker sits on its own series' candles). */
  private pastMarkers: ISeriesMarkersPluginApi<Time>;
  private patternKey = '';
  private patternPast: PastCandle[] | undefined;
  private pastVersion = 0;
  private patternDraw: PatternDraw[] = [];
  /** The patterns found on the completed candles shown, with each one's times (for the page's summary). */
  patternsFound: (ChartPattern & { breakT: number | null })[] = [];
  /** How many completed candles the patterns were found in. */
  patternsN = 0;
  /** Called when the patterns are recomputed (the page lists them under the chart). */
  onPatterns: (() => void) | null = null;

  constructor(el: HTMLElement) {
    this.chart = createChart(el, {
      autoSize: true,
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderVisible: false, scaleMargins: { top: 0.1, bottom: 0.26 } },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false, rightOffset: 4, fixLeftEdge: true },
    });
    this.pastCandles = this.chart.addSeries(CandlestickSeries, {
      priceLineVisible: false, lastValueVisible: false,
      priceFormat: { type: 'custom', minMove: 0.05, formatter: (p: number) => (p <= 0 ? '' : p.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) },
    });
    this.candles = this.chart.addSeries(CandlestickSeries, {
      priceLineVisible: true, lastValueVisible: true,
      priceFormat: { type: 'custom', minMove: 0.05, formatter: (p: number) => (p <= 0 ? '' : p.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })) },
    });
    // The time scale keeps its candle spacing on resize; after full screen, show the whole day again.
    this.chart.timeScale().subscribeSizeChange(() => {
      if (performance.now() < this.refitUntil) this.reset();
    });
    this.candles.attachPrimitive(this.profile);
    this.candles.attachPrimitive(this.zones);
    this.markers = createSeriesMarkers(this.candles, []);
    this.patternLayer = new PatternLayer(this.chart);
    this.planMarks = new PlanMarks(this.candles);
    this.tradeLayer = new TradeLayer(this.chart);
    this.pastMarkers = createSeriesMarkers(this.pastCandles, []);
    this.pastVolume = this.chart.addSeries(HistogramSeries, { priceScaleId: 'vol', priceFormat: { type: 'volume' }, lastValueVisible: false, priceLineVisible: false });
    this.volume = this.chart.addSeries(HistogramSeries, { priceScaleId: 'vol', priceFormat: { type: 'volume' }, lastValueVisible: false, priceLineVisible: false });
    this.chart.timeScale().subscribeVisibleLogicalRangeChange((r) => {
      if (r && this.offset > 0 && r.from < 25) this.onNeedMore?.();
    });
    this.chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.86, bottom: 0 } });
    this.vwap = this.chart.addSeries(LineSeries, { lineWidth: 2, lineStyle: LineStyle.Dashed, priceLineVisible: false, lastValueVisible: true, title: 'VWAP', crosshairMarkerVisible: false });
    // A click inside a zone box selects it (the page shows why it is a zone).
    this.chart.subscribeClick((p) => {
      const d = this.last;
      if (!d || !p.point || p.logical == null) return;
      const price = this.candles.coordinateToPrice(p.point.y);
      const i = (Math.round(p.logical) - this.offset) * (d.k ?? 1);
      const hit = price == null ? undefined : [...this.shown].reverse().find((o) =>
        i >= o.idx && i <= (o.mitigated ? o.endIdx ?? d.t : d.t) && price <= o.top && price >= o.bottom);
      this.selected = hit ? hit.idx : null;
      this.update(d);
      this.onZone?.(hit ?? null);
    });
    this.applyTheme();
  }

  applyTheme() {
    const C = palette();
    this.chart.applyOptions({
      layout: { background: { type: ColorType.Solid, color: 'transparent' }, textColor: C.mute, fontFamily: '"JetBrains Mono Variable", ui-monospace, monospace', fontSize: 11, attributionLogo: false },
      grid: { vertLines: { color: C.gridSoft }, horzLines: { color: C.gridSoft } },
      crosshair: { vertLine: { color: C.mute, labelBackgroundColor: C.tagBg }, horzLine: { color: C.mute, labelBackgroundColor: C.tagBg } },
    });
    this.candles.applyOptions({ upColor: C.g, downColor: C.r, borderUpColor: C.g, borderDownColor: C.r, wickUpColor: C.g, wickDownColor: C.r });
    this.pastCandles.applyOptions({ upColor: C.g, downColor: C.r, borderUpColor: C.g, borderDownColor: C.r, wickUpColor: C.g, wickDownColor: C.r });
    if (this.pastData.length) this.paintPastVolume();
    this.vwap.applyOptions({ color: C.blue });
    if (this.last) this.update(this.last);
    this.planMarks?.repaint();
  }

  update(data: ProChartData) {
    this.last = data;
    const { a, d, t, market, date, layers } = data;
    const C = palette();
    const on = (l: ProLayer) => layers.has(l);
    const k = data.k ?? 1;
    // Candle j covers 5-minute bars j·k … j·k+k−1; a 5-minute index maps to its candle's time.
    const ctime = (j: number) => barTime(date, market, j * k);
    const time = (i: number) => ctime(Math.floor(i / k));
    const bars = aggregate(d.bars, k);
    const slots = Math.ceil(market.bars / k);
    const start = ctime(0);
    const kChanged = k !== this.lastK || date !== this.lastDate;
    let moved = 0, arrived = false;
    if (data.past !== this.lastPast || kChanged) {
      // Earlier sessions (plain candles), redrawn only when they change.
      const past = (data.past ?? []).filter((c) => c.t < start);
      const prevOffset = this.offset;
      this.pastData = past;
      this.offset = past.length;
      this.pastCandles.setData(past.map((c) => ({ time: c.t as UTCTimestamp, open: c.o, high: c.h, low: c.l, close: c.c })));
      this.paintPastVolume();
      moved = kChanged ? 0 : this.offset - prevOffset;
      arrived = !kChanged && prevOffset === 0 && this.offset > 0;
    }
    this.lastPast = data.past; this.lastK = k; this.lastDate = date;
    this.slots = slots;
    this.ctime = ctime;

    // Today's candles, plus empty slots for the rest of the session so the axis spans the whole day.
    this.candles.setData([
      ...bars.map((b, j) => ({ time: ctime(j), open: b.o, high: b.h, low: b.l, close: b.c })),
      ...Array.from({ length: Math.max(0, slots - bars.length) }, (_, n) => ({ time: ctime(bars.length + n) })),
    ]);
    this.volume.applyOptions({ visible: on('volume') });
    this.volume.setData(bars.map((b, j) => ({ time: ctime(j), value: b.v, color: alpha(b.c >= b.o ? C.g : C.r, 0.4) })));
    this.pastVolume.applyOptions({ visible: on('volume') });
    this.vwap.applyOptions({ visible: on('vwap') });
    this.vwap.setData(bars.map((_, j) => ({ time: ctime(j), value: d.vwap[Math.min(d.vwap.length - 1, j * k + k - 1, d.bars.length - 1)] })));

    // Order-flow pane: per-bar delta and the running total.
    const f = a.flow;
    if (on('flow') && f) {
      if (!this.delta) {
        this.delta = this.chart.addSeries(HistogramSeries, { priceLineVisible: false, lastValueVisible: false, priceFormat: { type: 'custom', formatter: signedVol, minMove: 1 }, title: 'Δ' }, 1);
        this.cvd = this.chart.addSeries(LineSeries, { lineWidth: 2, priceLineVisible: false, priceScaleId: 'cvd', priceFormat: { type: 'custom', formatter: signedVol, minMove: 1 }, title: 'CVD', crosshairMarkerVisible: false }, 1);
        this.chart.panes()[1]?.setStretchFactor(0.28);
      }
      this.delta.setData(bars.map((_, j) => {
        const g = f.delta.slice(j * k, j * k + k).filter((v): v is number => v !== null);
        if (!g.length) return { time: ctime(j) };
        const value = g.reduce((x, v) => x + v, 0);
        return { time: ctime(j), value, color: alpha(value >= 0 ? C.g : C.r, 0.7) };
      }));
      this.cvd!.applyOptions({ color: C.navy });
      this.cvd!.setData(bars.map((_, j) => {
        const v = f.cvd.slice(j * k, j * k + k).filter((x): x is number => x !== null).pop();
        return v === undefined ? { time: ctime(j) } : { time: ctime(j), value: v };
      }));
    } else if (this.delta) {
      this.chart.removeSeries(this.delta);
      this.chart.removeSeries(this.cvd!);
      this.delta = this.cvd = null;
      if (this.chart.panes().length > 1) this.chart.removePane(1);
    }

    // Horizontal levels as price lines with labels on the price scale.
    for (const l of this.lines) this.candles.removePriceLine(l);
    this.lines = [];
    const line = (price: number, title: string, color: string, style: LineStyle, axis = true) =>
      this.lines.push(this.candles.createPriceLine({ price, title, color, lineStyle: style, lineWidth: 1, axisLabelVisible: axis }));
    if (on('levels')) {
      const target = a.bull ? C.g : C.r;
      const color: Record<SRKey, string> = { R1: a.bull ? target : C.amber, R2: a.bull ? target : C.mute, S1: a.bull ? C.amber : target, S2: a.bull ? C.mute : target };
      for (const k of ['R2', 'R1', 'S1', 'S2'] as SRKey[]) line(d.levels[k], SWING_NAME[k], color[k], d.projected.has(k) ? LineStyle.Dotted : LineStyle.Dashed);
    }
    if (on('pivots') && data.prev?.high && data.prev.low) {
      const p = classicPivots(data.prev.high, data.prev.low, data.prev.close);
      if (p) {
        line(p.P, 'P', C.mute, LineStyle.Solid);
        for (const k of ['R1', 'R2', 'R3'] as const) line(p[k], k, alpha(C.r, 0.85), LineStyle.LargeDashed);
        for (const k of ['S1', 'S2', 'S3'] as const) line(p[k], k, alpha(C.g, 0.85), LineStyle.LargeDashed);
      }
    }
    if (on('liquidity')) {
      for (const l of d.liquidity.filter((x) => Math.abs(x.distPct) <= 2)) line(l.p, l.key === 'RND' ? '' : l.key, '#a855f7', l.key === 'RND' ? LineStyle.Dotted : LineStyle.SparseDotted, false);
    }
    const zPrice = d.plan ? d.plan.z : d.zone ? (d.zone.type === 'bull' ? d.zone.top : d.zone.bottom) : null;
    if (on('zones') && zPrice != null) line(zPrice, 'Z', C.ink, LineStyle.Solid);

    // Volume profile over the day's range so far; the busiest price (point of control) gets a line.
    const vp = a.profile;
    if (on('profile') && vp && a.high > a.low) {
      const bins = vp.buy.length, step = (a.high - a.low) / bins;
      const rows = vp.buy.map((b, i) => ({ bottom: a.low + step * i, top: a.low + step * (i + 1), buy: b, sell: vp.sell[i] }));
      let poc = -1, best = 0;
      rows.forEach((r, i) => { if (r.buy + r.sell > best) { best = r.buy + r.sell; poc = i; } });
      this.profile.set(rows, poc, { buy: C.g, sell: C.r });
      if (poc >= 0) line((rows[poc].top + rows[poc].bottom) / 2, 'POC', C.mute, LineStyle.Dotted);
    } else this.profile.set([], -1, { buy: C.g, sell: C.r });

    // Zones: the active ones, plus the latest few broken ones for context.
    this.shown = on('zones') ? a.obs.filter((o, i, all) => !o.mitigated || i >= all.length - 3) : [];
    this.zones.set(this.shown.map((o) => ({
      from: time(o.idx), to: time(o.mitigated ? Math.min((o.endIdx ?? t) + k, market.bars - 1) : Math.min(t, market.bars - 1)),
      top: o.top, bottom: o.bottom, color: o.type === 'bull' ? C.gd : C.rd, dashed: o.mitigated,
      fill: o.mitigated ? 0.05 : d.zone && o.idx === d.zone.idx ? 0.18 : 0.1,
      label: zoneLabel(o), selected: o.idx === this.selected,
      full: o.wide ? { top: o.fullTop, bottom: o.fullBottom } : undefined,
    })));

    // Chart patterns over all the candles shown: earlier sessions, then today's completed ones (a candle
    // still forming has no close yet). Recomputed only when a candle completes or history changes.
    const done = bars.length - (d.bars.length < market.bars ? 1 : 0);
    const from = data.patternsFrom ?? 0;
    // The key covers the candles' values too: today's candles can be replaced with the same count.
    if (data.past !== this.patternPast) { this.patternPast = data.past; this.pastVersion++; }
    let sum = 0; for (let j = 0; j < done; j++) sum += bars[j].o + bars[j].h + bars[j].l + bars[j].c;
    const pKey = data.patterns ? `${this.pastVersion}|${from}|${k}|${date}|${done}|${sum}` : '';
    if (pKey !== this.patternKey) {
      this.patternKey = pKey;
      if (!data.patterns) { this.patternsFound = []; this.patternDraw = []; this.patternsN = 0; }
      else {
        const cs = [...this.pastData.filter((c) => c.t >= from).map((c) => ({ t: c.t, o: c.o, h: c.h, l: c.l, c: c.c, v: c.v })), ...bars.slice(0, Math.max(0, done)).map((b, j) => ({ t: ctime(j) as number, o: b.o, h: b.h, l: b.l, c: b.c, v: b.v }))];
        const found = chartPatterns(cs.map((c) => ({ date: '', o: c.o, h: c.h, l: c.l, c: c.c, v: c.v })));
        const T = (i: number) => cs[i].t;
        this.patternsFound = found.map((p) => ({ ...p, breakT: p.breakIdx === null ? null : T(p.breakIdx) }));
        this.patternsN = cs.length;
        this.patternDraw = found.map((p) => ({ name: p.name, pts: p.points.map((x) => ({ t: T(x.i), v: x.p })), level: [{ t: T(p.level.from.i), v: p.level.from.p }, { t: T(p.level.to.i), v: p.level.to.p }], breakT: p.breakIdx === null ? null : T(p.breakIdx) }));
      }
      this.onPatterns?.();
    }
    const patternMarks = [...this.patternLayer.set(this.patternDraw), ...this.tradeLayer.set(this.tradeDraw)].sort((x, y) => (x.time as number) - (y.time as number));
    this.pastMarkers.setMarkers(patternMarks.filter((m) => (m.time as number) < start));

    // Sweeps (today), and the patterns' breakouts.
    this.markers.setMarkers([...(on('sweeps') ? a.sweeps.filter((w) => w.idx < d.bars.length).map((w) => ({
      time: time(w.idx), position: w.type === 'bear' ? 'aboveBar' as const : 'belowBar' as const,
      shape: w.type === 'bear' ? 'arrowDown' as const : 'arrowUp' as const, color: w.failed ? C.mute : C.amber,
      text: `${w.failed ? '✕ ' : ''}swept ${w.key}`,
    })) : []), ...patternMarks.filter((m) => (m.time as number) >= start)].sort((x, y) => (x.time as number) - (y.time as number)));

    if (this.pendingSessions !== null && data.pastLoaded !== false) {
      // A range button: show that many sessions once this timeframe's history is in.
      const n = this.pendingSessions; this.pendingSessions = null; this.pending = null;
      this.showSessions(n);
      this.fitted = true;
    } else if (this.pending && data.pastLoaded !== false) {
      // A timeframe switch: keep the window the user was looking at (once this timeframe's data is in).
      const vp = this.pending; this.pending = null;
      this.setViewport(vp);
      this.fitted = true;
    } else if (!this.fitted || (kChanged && !this.pending && this.pendingSessions === null)) {
      this.reset();
      this.fitted = true;
    } else if (arrived && !this.pending && this.pendingSessions === null) {
      // Earlier sessions just arrived: show today's session, with the past a scroll away to the left.
      this.reset();
    }
    // More history arriving on the left needs nothing here: the time scale is anchored to the right,
    // so the same candles stay in view (shifting the range as well would throw the view forward).
    void moved;
  }

  private ctime: (j: number) => UTCTimestamp = () => 0 as UTCTimestamp;
  private paintPastVolume() {
    const C = palette();
    this.pastVolume.setData(this.pastData.map((c) => ({ time: c.t as UTCTimestamp, value: c.v, color: alpha(c.c >= c.o ? C.g : C.r, 0.25) })));
  }
  /** The time of the candle at logical index i (earlier sessions, then today's slots), or null. */
  private timeAt(i: number): number | null {
    if (i < 0) return null;
    if (i < this.offset) return this.pastData[i]?.t ?? null;
    return i - this.offset < this.slots ? this.ctime(i - this.offset) : null;
  }
  /** The last logical index whose candle starts at or before time t. */
  private indexAtOrBefore(t: number) {
    let lo = 0, hi = this.offset + this.slots - 1, ans = 0;
    while (lo <= hi) { const mid = (lo + hi) >> 1; const mt = this.timeAt(mid); if (mt !== null && mt <= t) { ans = mid; lo = mid + 1; } else hi = mid - 1; }
    return ans;
  }
  /** The visible window: its right edge as a time, how many candles wide, and whether it is at the live end. */
  getViewport(): Viewport | null {
    const r = this.chart.timeScale().getVisibleLogicalRange();
    if (!r) return null;
    const lastReal = this.offset + Math.ceil((this.last?.d.bars.length ?? 0) / (this.last?.k ?? 1)) - 1;
    const i = Math.max(0, Math.min(Math.round(r.to), this.offset + this.slots - 1));
    return { right: this.timeAt(i) ?? 0, bars: Math.max(10, r.to - r.from), atEnd: r.to >= lastReal - 1 };
  }
  /** Show a window: the live end, or the candle at the given right-edge time, with the same candle count. */
  setViewport(vp: Viewport) {
    const ts = this.chart.timeScale();
    const to = vp.atEnd ? this.offset + this.slots - 1 + 3 : Math.max(this.indexAtOrBefore(vp.right), Math.min(vp.bars, this.offset + this.slots - 1));
    ts.setVisibleLogicalRange({ from: to - vp.bars, to });
  }
  /** Keep this window when the next timeframe's data arrives. */
  requestViewport(vp: Viewport | null) { this.pending = vp; }
  /** Show the last n sessions once the next timeframe's (or a longer) history arrives. */
  requestSessions(n: number | null) { this.pendingSessions = n; }
  /** The candle under the crosshair: an earlier session's, or today's index. */
  readout(logical: number | null): { past?: PastCandle; today?: number } {
    if (logical == null) return {};
    if (logical >= 0 && logical < this.offset) return { past: this.pastData[logical] };
    return { today: logical - this.offset };
  }
  /** Show the last n sessions (today's included), ending at the end of today's session. */
  showSessions(n: number) {
    const to = this.offset + this.slots - 1 + 3;
    this.chart.timeScale().setVisibleLogicalRange({ from: Math.max(-2, to - n * this.slots - 3), to });
  }

  /** Paper trades to draw (times snapped to this chart's candles with snapTime). */
  setTrades(list: TradeDraw[]) { this.tradeDraw = list; if (this.last) this.update(this.last); }
  /** The start of the candle containing time t (null outside the loaded candles). */
  snapTime(t: number): number | null {
    const n = this.offset + this.slots;
    if (!n || t < (this.timeAt(0) ?? Infinity)) return null;
    return this.timeAt(this.indexAtOrBefore(t));
  }

  /** The desk's plan levels (an empty list clears them). */
  setMarks(m: PlanMark[]) { this.planMarks.set(m); }

  zoom(factor: number) {
    const ts = this.chart.timeScale(), r = ts.getVisibleLogicalRange();
    if (!r) return;
    const right = r.to, width = (r.to - r.from) * factor;
    ts.setVisibleLogicalRange({ from: Math.max(-2, right - Math.max(8, width)), to: right });
  }
  /** Show today's whole session (earlier sessions stay to the left, a scroll away). */
  reset() {
    if (!this.offset) { this.chart.timeScale().fitContent(); return; }
    this.chart.timeScale().setVisibleLogicalRange({ from: this.offset - 0.5, to: this.offset + this.slots + 3 });
  }

  /** Price scale: normal, logarithmic, or % from the first visible candle. */
  setScale(s: 'normal' | 'log' | 'pct') {
    this.chart.priceScale('right').applyOptions({ mode: s === 'log' ? PriceScaleMode.Logarithmic : s === 'pct' ? PriceScaleMode.Percentage : PriceScaleMode.Normal });
  }
  /** Fit the whole day on the size changes that follow (full screen resizes in more than one step). */
  refitAfterResize() { this.refitUntil = performance.now() + 1000; }
  destroy() { this.chart.remove(); }
}
